/*
 * What a rep has typed and not yet sent, kept per conversation in this browser (master-inbox plan,
 * step 5, D6: "Unsent text kept per conversation"). It survives J and K, a click on another row, a
 * move to another page and a reload; it goes when the reply is sent, when the rep empties the box,
 * when they sign out, or a week after it was last touched. Keyed by person, workspace and
 * conversation, so nobody else signed in to this browser is shown it.
 *
 * Storage can be missing or refuse a write (blocked site data, a full quota). That is reported, not
 * hidden: the box then asks before leaving (`leave-confirm.tsx`), because the text exists only on
 * screen. Whatever storage holds is parsed before use; it is this browser's, but not this code's.
 */
import { z } from "zod";
import { ComposerThreadVersionSchema } from "../../src/personal-composer-contract.ts";
import {
  type BoxContent,
  editorHtml,
  paragraphs,
  sameBoxText,
  type WholeDraft,
} from "./reply-box-link.ts";

const PREFIX = "leadcue-reply-inbox-reply";
const KEPT_FOR_MS = 7 * 24 * 3_600_000;

const UnsentReplySchema = z.strictObject({
  /** The lead message the text was written to; a newer one makes the text worth a second look. */
  messageId: z.string().nullable(),
  format: z.enum(["text", "html"]),
  /** Plain text, always; the editor's HTML too when the format is Formatted. */
  text: z.string(),
  html: z.string(),
  /** The draft revision "Edit in reply box" put here, edited since or not (step 8c). */
  fromDraft: z.number().int().nonnegative().optional(),
  /** The personal server revision these local words were based on, including unsaved edits. */
  composerRevision: z.number().int().nonnegative().optional(),
  composerManaged: z.literal(true).optional(),
  composerThreadVersion: ComposerThreadVersionSchema.optional(),
  at: z.iso.datetime(),
});
export type UnsentReply = z.infer<typeof UnsentReplySchema>;

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem" | "key" | "length">;

/** The browser's storage, or null where reading it throws (a sandboxed frame, blocked site data). */
function browserStore(): Store | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function unsentKey(userId: string, workspaceId: string, planId: string): string {
  return [PREFIX, userId, workspaceId, planId].map(encodeURIComponent).join(":");
}

/**
 * Forgets everything this person kept, in every workspace, as they sign out: the next person to use
 * this browser never inherits a reply half written to someone else's lead. Others' text stays. The
 * number forgotten, or null where storage could not be read.
 */
export function forgetUnsent(userId: string, store: Store | null = browserStore()): number | null {
  if (!store) return null;
  const mine = `${[PREFIX, userId].map(encodeURIComponent).join(":")}:`;
  try {
    const keys: string[] = [];
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (key?.startsWith(mine)) keys.push(key);
    }
    for (const key of keys) store.removeItem(key);
    return keys.length;
  } catch {
    return null;
  }
}

export function readUnsent(key: string, now = Date.now()): UnsentReply | null {
  try {
    const raw = browserStore()?.getItem(key);
    if (!raw) return null;
    const parsed = UnsentReplySchema.safeParse(JSON.parse(raw));
    if (!parsed.success || now - Date.parse(parsed.data.at) > KEPT_FOR_MS) return null;
    return parsed.data.text.trim() ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Keeps `reply`, or forgets it when it is null or blank. False: storage refused, nothing is kept. */
export function keepUnsent(key: string, reply: UnsentReply | null): boolean {
  const store = browserStore();
  if (!store) return false;
  try {
    if (reply?.text.trim()) store.setItem(key, JSON.stringify(reply));
    else store.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/** An acknowledgement cannot replace words typed after its request started. */
export function acknowledgeUnsent(
  key: string,
  captured: {
    format: "text" | "html";
    plainText: string;
    html: string;
    fromDraft: number | null;
  } | null,
  revision: number,
  threadVersion: string,
): void {
  const kept = readUnsent(key);
  if (!kept || !captured) return;
  if (
    kept.format !== captured.format ||
    kept.text !== captured.plainText ||
    (kept.format === "html" && kept.html !== captured.html) ||
    (kept.fromDraft ?? null) !== captured.fromDraft
  )
    return;
  keepUnsent(key, {
    ...kept,
    composerManaged: true,
    composerRevision: revision,
    composerThreadVersion: threadVersion,
  });
}

/** What is kept, if it is still exactly the snapshot a pending save captured. */
function keptAsCaptured(key: string, snapshot: BoxContent): UnsentReply | null {
  const kept = readUnsent(key);
  if (
    !kept ||
    !sameBoxText(snapshot, {
      format: kept.format,
      text: kept.text,
      html: kept.format === "html" ? kept.html : paragraphs(kept.text),
      loaded: kept.fromDraft ?? null,
      open: false,
    })
  )
    return null;
  return kept;
}

/** A late save may acknowledge only the originating conversation's unchanged kept snapshot. */
export function adoptUnsent(key: string, snapshot: BoxContent, revision: number): void {
  const kept = keptAsCaptured(key, snapshot);
  if (kept) keepUnsent(key, { ...kept, fromDraft: revision });
}

/**
 * A late "Change ending" keeps its whole revision for the conversation it was made in, in the format
 * kept, where the kept text is still what the save captured. Formatted keeps the draft's plain words
 * as its text until an editor loads the markup and reads its own.
 */
export function replaceUnsent(key: string, snapshot: BoxContent, draft: WholeDraft): void {
  const kept = keptAsCaptured(key, snapshot);
  if (!kept) return;
  keepUnsent(key, {
    ...kept,
    text: draft.plainText,
    html: kept.format === "html" ? editorHtml(draft.html) : "",
    fromDraft: draft.revision,
    at: new Date().toISOString(),
  });
}

/*
 * The reply box's files live on the server (`reply-attachment-store.ts`); this browser keeps only the
 * box's id, beside the text and under the same key, so signing out forgets it with the text and a box
 * holding files and no words is still found again. Never a file's bytes or name.
 */
const ComposerSchema = z.strictObject({ composerId: z.uuid(), at: z.iso.datetime() });

const composerKey = (key: string) => `${key}:files`;

/** The id of this conversation's reply box with files in it, or null when there is none kept. */
export function readComposer(key: string, now = Date.now()): string | null {
  try {
    const raw = browserStore()?.getItem(composerKey(key));
    if (!raw) return null;
    const parsed = ComposerSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || now - Date.parse(parsed.data.at) > KEPT_FOR_MS) return null;
    return parsed.data.composerId;
  } catch {
    return null;
  }
}

/** Keeps the box's id, renewed, or forgets it with null. False: storage refused. */
export function keepComposer(key: string, composerId: string | null): boolean {
  const store = browserStore();
  if (!store) return false;
  try {
    if (composerId)
      store.setItem(composerKey(key), JSON.stringify({ composerId, at: new Date().toISOString() }));
    else store.removeItem(composerKey(key));
    return true;
  } catch {
    return false;
  }
}
